import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/system";
import Layout from "../../Components/Layout";
import AIAnswerCard from "../../Components/AISearch/AIAnswerCard";
import SearchResultsList from "../../Components/AISearch/SearchResultsList";
import { useAISearch } from "../../hooks/useAISearch";

const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** True when AI response indicates no data found (hide sources and results list). */
function isNoDataResponse(content, sources = [], searchResults = []) {
    const noDataPhrases = /couldn't find|could not find|didn't find|no (data|results?|information|articles?)\s*(found)?/i;
    const contentSaysNoData = content && noDataPhrases.test(content);
    const hasNoSourcesOrResults = (!sources || sources.length === 0) && (!searchResults || searchResults.length === 0);
    return contentSaysNoData || hasNoSourcesOrResults;
}

/**
 * /search page — AI-powered search via POST v1/article/search (Bearer token).
 */
export default function SearchPage() {
    const [searchParams] = useSearchParams();
    const query = searchParams.get("q") || "";

    const {
        displayedText,
        isLoading,
        sources,
        searchResults,
        startSearch,
        followUpSearch,
    } = useAISearch();

    const [followUps, setFollowUps] = useState([]);
    const isFollowUpLoading = followUps.some((f) => f.isLoading);
    const [searchError, setSearchError] = useState(null);
    const prevQueryRef = useRef("");

    useEffect(() => {
        if (!query) return;
        setSearchError(null);
        if (query === prevQueryRef.current) return;
        prevQueryRef.current = query;
        setFollowUps([]);
        startSearch(query);
    }, [query, startSearch]);

    const handleFollowUp = useCallback(
        (questionText) => {
            const followUpKey = Date.now();
            setFollowUps((prev) => [
                ...prev,
                {
                    key: followUpKey,
                    question: questionText,
                    displayedText: "",
                    isLoading: true,
                },
            ]);
            followUpSearch(questionText, {
                onChunk: (chunk) => {
                    setFollowUps((prev) =>
                        prev.map((f) =>
                            f.key === followUpKey
                                ? { ...f, displayedText: f.displayedText + chunk }
                                : f
                        )
                    );
                },
                onDone: () => {
                    setFollowUps((prev) =>
                        prev.map((f) =>
                            f.key === followUpKey
                                ? { ...f, isLoading: false }
                                : f
                        )
                    );
                },
            });
        },
        [followUpSearch]
    );

    if (!query) {
        return (
            <Layout>
                <Box sx={{ maxWidth: 800, mx: "auto", px: 3, py: 8, textAlign: "center" }}>
                    <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: 18, color: "#94a3b8" }}>
                        Enter a search query to get started.
                    </Typography>
                </Box>
            </Layout>
        );
    }

    const showLoading = isLoading && !displayedText && !searchError;
    const hasNoData = isNoDataResponse(displayedText, sources, searchResults);

    return (
        <Layout>
            <Box
                sx={{
                    maxWidth: 1000,
                    mx: "auto",
                    fontFamily: "'Inter', sans-serif",
                }}
            >
                {searchError && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: "#fef2f2", borderRadius: 1 }}>
                        <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#b91c1c" }}>
                            {searchError}
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        animation: `${fadeSlideUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both`,
                        animationDelay: "0.1s",
                    }}
                >
                    <AIAnswerCard
                        query={query}
                        displayedText={displayedText}
                        isLoading={showLoading || isLoading}
                        sources={sources}
                        followUps={followUps}
                        onFollowUp={handleFollowUp}
                        isFollowUpLoading={isFollowUpLoading}
                        disableFollowUp={false}
                        showSources={!hasNoData}
                    />
                </Box>

                {!hasNoData && (
                    <Box
                        sx={{
                            animation: `${fadeSlideUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both`,
                            animationDelay: "0.25s",
                        }}
                    >
                        <SearchResultsList results={searchResults} query={query} />
                    </Box>
                )}
            </Box>
        </Layout>
    );
}
